import { isNotNullish } from '@airreading/common';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export function assert(condition: boolean, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

export function assertNotNullish<T>(value: T | undefined | null, message?: string): asserts value is T {
  assert(isNotNullish(value), message || 'Value is nullish');
}

export type DebouncedAccessor<Data> = readonly [[liveData: Data, debouncedData: Data], Dispatch<SetStateAction<Data>>];
export type Setter<Data> = (data: Data) => void;

/**
 * Like `useState`, but with the restriction that this is meant to wrap around
 * some other existing, original state whose immediate change would trigger
 * some sort of request. This wrapper hook exposes both the live state and the
 * debounced state, as well as a replacement setter that updates the original
 * state for you on a debounced basis.
 * @param initOrDebouncedValue a state variable from the original state
 * @param debouncedSetter a setter function that updates the original state
 * @returns
 */
export function useDebouncedInput<Input = string>(
  initOrDebouncedValue: Input | undefined,
  debouncedSetter: (value: Input | undefined) => unknown,
  debounceDelay: number = 500,
): DebouncedAccessor<Input | undefined> {
  const [liveValue, setValue] = useState(initOrDebouncedValue);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      debouncedSetter(liveValue);
    }, debounceDelay);
    return () => clearTimeout(timeoutId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveValue]);

  return [[liveValue, initOrDebouncedValue], setValue];
}

export function isNotErrorString<T>(inputOrError: T | string): inputOrError is T {
  return typeof inputOrError !== 'string';
}

export type DeployedEnvironment = 'local' | 'preview' | 'main' | 'staging' | 'prod';

const KNOWN_ENVIRONMENTS = new Set<DeployedEnvironment>(['local', 'preview', 'main', 'staging', 'prod']);

/**
 * Normalizes a raw environment string into a canonical {@link DeployedEnvironment}.
 * Tolerates the legacy aliases that older builds used to emit (e.g. `production`,
 * `devqa`) so any stale config keeps resolving to the right canonical name.
 */
function toDeployedEnvironment(value: string | undefined): DeployedEnvironment | undefined {
  if (!value) {
    return undefined;
  }
  if (KNOWN_ENVIRONMENTS.has(value as DeployedEnvironment)) {
    return value as DeployedEnvironment;
  }
  switch (value) {
    case 'production':
      return 'prod';
    case 'devqa':
    case 'development':
    case 'dev':
      return 'main';
    default:
      return undefined;
  }
}

const environmentOverride = toDeployedEnvironment(import.meta.env.VITE_OVERRIDE_DEPLOYED_ENVIRONMENT);
const buildEnvironment = toDeployedEnvironment(import.meta.env.VITE_APP_ENV);

/**
 * Resolves the deployed environment for the running client.
 *
 * Resolution order:
 * 1. Explicit override (`VITE_OVERRIDE_DEPLOYED_ENVIRONMENT`) to simulate a deployed env locally.
 * 2. Canonical environment baked into the build (`VITE_APP_ENV`, set by CI to match the server's
 *    `APP_ENV` / deploy `env_name`). This is the source of truth in every deployed client and
 *    keeps Sentry env names aligned with the server.
 * 3. Hostname fallback when `VITE_APP_ENV` is unavailable at build time.
 */
export function getEnvironmentLevel(): DeployedEnvironment {
  if (environmentOverride) {
    return environmentOverride;
  }

  if (buildEnvironment) {
    return buildEnvironment;
  }

  const { hostname } = window.location;

  if (hostname === 'localhost') {
    return 'local';
  }

  if (hostname.endsWith('portal.airreading.com')) {
    return 'prod';
  }

  if (hostname.endsWith('staging.airreadingclass.com')) {
    return 'staging';
  }

  if (hostname.startsWith('preview') && hostname.endsWith('.qa.airreadingclass.com')) {
    return 'preview';
  }

  if (hostname.endsWith('.qa.airreadingclass.com')) {
    return 'main';
  }

  console.warn(`Unknown hostname: ${hostname}, defaulting to staging for Sentry`);
  return 'staging';
}

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};
